import { h } from 'vue';
import { AxiosError } from 'axios';
import { toast } from 'vue3-toastify';
import ErrorToast from '@/components/common/ErrorToast.vue';

interface ErrorResponse {
    id?: string;
    error?: {
        id?: string;
        message: string;
        severity?: 'warning' | 'error';
    };
    warning?: {
        id?: string;
        message: string;
        severity?: 'warning' | 'error';
    };
}

const showError = (
    message: string,
    errorId?: string,
    severity: 'error' | 'warning' = 'error',
): void => {
    if (errorId) {
        toast(() => h(ErrorToast, { message, errorId, severity }), {
            type: severity,
            autoClose: false,
            closeButton: true,
        });
    } else {
        toast(message, { type: severity });
    }
};

export default async function errorHandler(error: AxiosError): Promise<undefined> {
    if (error.response) {
        let responseData = error.response.data as ErrorResponse;

        // When responseType is 'blob', error response data arrives as a Blob — parse it back to JSON
        if (error.response.data instanceof Blob) {
            try {
                const text = await new Response(error.response.data as Blob).text();
                responseData = JSON.parse(text) as ErrorResponse;
            } catch (parseError) {
                console.error('Failed to parse Blob error response:', parseError);
                showError('An unexpected error occurred');
                return undefined;
            }
        }

        const payload = responseData.error || responseData.warning;
        const errorId = payload?.id ?? responseData?.id;
        const message = Array.isArray(payload?.message) ? payload?.message[0] : payload?.message;
        const severity = payload?.severity ?? (responseData.error ? 'error' : 'warning');

        // Check if error comes from server
        if (errorId || message) {
            showError(message as string, errorId, severity);
        }
    } else if (error.request) {
        showError(error.message + ': ' + error.code);
    } else {
        showError(error.message);
    }
    return undefined;
}
